import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Plus } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Pagination } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import type { ColumnDef } from '@vnetwork/ui/data';
import { useSurveys, type SurveyRecord } from '@/api/surveys';
import { SurveysToolbar } from './surveys-toolbar';
import { paginationView } from '@/pages/customers/pagination-summary';

const PAGE_SIZE = 20;

/** Debounce a fast-changing value to throttle server refetches. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/** Format a nullable 1-10 score as text. */
function fmtScore(v: number | null | undefined): string {
  return v != null ? String(v) : '—';
}

/** Format a timestamp from the API (ISO without Z — treat as UTC). */
function fmtDate(iso: string): string {
  const d = new Date(iso + 'Z');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const COLUMNS: ColumnDef<SurveyRecord, unknown>[] = [
  {
    id: 'survey_record_id',
    header: '#',
    accessorFn: (r) => r.survey_record_id,
    cell: ({ getValue }) => (
      <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
        #{String(getValue())}
      </span>
    ),
    size: 70,
  },
  {
    id: 'customer_name',
    header: 'Customer',
    accessorFn: (r) => r.customer_name,
    cell: ({ getValue }) => (
      <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{String(getValue())}</span>
    ),
  },
  {
    id: 'employee_name',
    header: 'Surveyor',
    accessorFn: (r) => r.employee_name,
  },
  {
    id: 'average_score',
    header: 'Avg Score',
    accessorFn: (r) => r.average_score,
    cell: ({ getValue }) => fmtScore(getValue() as number | null),
    size: 110,
  },
  {
    id: 'score_service_quality',
    header: 'Service Quality',
    accessorFn: (r) => r.score_service_quality,
    cell: ({ getValue }) => fmtScore(getValue() as number | null),
    size: 130,
  },
  {
    id: 'score_customer_care',
    header: 'Customer Care',
    accessorFn: (r) => r.score_customer_care,
    cell: ({ getValue }) => fmtScore(getValue() as number | null),
    size: 130,
  },
  {
    id: 'score_tech_support',
    header: 'Tech Support',
    accessorFn: (r) => r.score_tech_support,
    cell: ({ getValue }) => fmtScore(getValue() as number | null),
    size: 120,
  },
  {
    id: 'score_issue_resolution',
    header: 'Issue Resolution',
    accessorFn: (r) => r.score_issue_resolution,
    cell: ({ getValue }) => fmtScore(getValue() as number | null),
    size: 140,
  },
  {
    id: 'score_referral_willingness',
    header: 'Referral',
    accessorFn: (r) => r.score_referral_willingness,
    cell: ({ getValue }) => fmtScore(getValue() as number | null),
    size: 90,
  },
  {
    id: 'upsale_potential',
    header: 'Upsale',
    accessorFn: (r) => r.upsale_potential,
    cell: ({ getValue }) => {
      const v = getValue() as string[];
      return v?.length ? v.join(', ') : '—';
    },
    size: 110,
  },
  {
    id: 'created_at',
    header: 'Created',
    accessorFn: (r) => r.created_at,
    cell: ({ getValue }) => fmtDate(String(getValue())),
    size: 110,
  },
];

/**
 * `/surveys` — paginated list of survey records. Toolbar: search + Display options.
 * Clicking a row navigates to the record detail page.
 */
export default function SurveysPage() {
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);

  const [visible, setVisible] = useState<Record<string, boolean>>(
    () => Object.fromEntries(COLUMNS.map((c) => [c.id as string, true])),
  );

  const { data, isLoading, isError, isFetching, refetch } = useSurveys({
    page,
    page_size: PAGE_SIZE,
    search: query || undefined,
  });

  const rows = data?.results ?? [];
  const pagination = data?.pagination;
  const pageCount = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? rows.length;

  const visibleColumns = useMemo(
    () => COLUMNS.filter((c) => visible[c.id as string] !== false),
    [visible],
  );

  const onSearch = (value: string) => {
    setRawQuery(value);
    setPage(1);
  };

  return (
    <Container size="full">
      <PageHeader
        title="Surveys"
        description="Customer satisfaction survey records."
        actions={
          <Button
            startIcon={<Plus size={15} />}
            onClick={() => navigate('/surveys/records/new')}
          >
            New record
          </Button>
        }
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <SurveysToolbar
          columns={COLUMNS}
          query={rawQuery}
          onQueryChange={onSearch}
          visible={visible}
          onToggleColumn={(id, checked) =>
            setVisible((prev) => ({ ...prev, [id]: checked }))
          }
        />

        {isError ? (
          <Alert
            tone="danger"
            title="Failed to load survey records"
            actions={
              <Button size="sm" variant="ghost" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        ) : isLoading ? (
          <Card flat>
            <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} style={{ height: 24 }} />
              ))}
            </CardBody>
          </Card>
        ) : (
          <DataTable
            columns={visibleColumns}
            data={rows}
            getRowId={(r) => String(r.survey_record_id)}
            onRowClick={(r) => navigate(`/surveys/records/${r.survey_record_id}`)}
            emptyState={
              <EmptyState
                icon={<ClipboardList size={24} />}
                title="No survey records found"
                description="Try adjusting your search, or create a new record."
              />
            }
            footer={(() => {
              const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
              return (
                <Pagination
                  page={page}
                  pageCount={view.pageCount}
                  onPageChange={setPage}
                  summary={`${view.summary}${isFetching ? ' …' : ''}`}
                />
              );
            })()}
          />
        )}
      </div>
    </Container>
  );
}
